/**
 * =============================================================================
 * Hook: useCountdown
 * Versão: 1.0.0 (2026-05-01)
 *
 * Descrição:
 *   Contador regressivo reutilizável e leve, com tick adaptativo.
 *   Pensado para badges de escassez (campanhas, ofertas, leilões etc.).
 *
 *   - Tick adaptativo:
 *       • a cada 1 segundo  quando faltam ≤ 1 hora
 *       • a cada 30 segundos quando faltam ≤ 24 horas
 *       • a cada 60 segundos acima disso
 *   - Pausa quando a aba está oculta (`document.hidden`) e retoma ao voltar.
 *   - Toda lógica é client-side (Date.now() vs target em UTC); não depende
 *     de timezone para exibir o delta.
 * =============================================================================
 */
import { useEffect, useRef, useState } from 'react';

/** Estados possíveis de um contador. */
export type CountdownStatus =
  | 'idle'        // sem data alvo
  | 'not_started' // antes de startsAt
  | 'running'     // entre start e end (> 24h)
  | 'urgent'      // ≤ 24h restantes
  | 'critical'    // ≤ 1h restantes
  | 'ended';      // após end

export interface CountdownResult {
  status: CountdownStatus;
  /** Milissegundos restantes até o alvo (>= 0). */
  totalMs: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Data alvo (Date) já parseada, ou null. */
  target: Date | null;
}

interface Options {
  /** Início opcional — quando informado, antes dele status = 'not_started'. */
  startsAt?: string | Date | null;
}

/** Constantes em ms — evita recriar a cada render. */
const SEC = 1_000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Calcula o estado a partir do delta. */
function computeStatus(deltaMs: number, hasStartFuture: boolean): CountdownStatus {
  if (hasStartFuture) return 'not_started';
  if (deltaMs <= 0) return 'ended';
  if (deltaMs <= HOUR) return 'critical';
  if (deltaMs <= DAY) return 'urgent';
  return 'running';
}

/** Decompõe ms em d/h/m/s (sempre não-negativo). */
function decompose(ms: number) {
  const safe = Math.max(0, ms);
  return {
    days: Math.floor(safe / DAY),
    hours: Math.floor((safe % DAY) / HOUR),
    minutes: Math.floor((safe % HOUR) / MIN),
    seconds: Math.floor((safe % MIN) / SEC),
  };
}

/** Define o intervalo do próximo tick conforme o tempo restante. */
function nextInterval(deltaMs: number): number {
  if (deltaMs <= HOUR) return SEC;       // 1s
  if (deltaMs <= DAY) return 30 * SEC;   // 30s
  return 60 * SEC;                        // 60s
}

/**
 * Retorna o estado atual do countdown para `endsAt` (ISO ou Date).
 * Quando `endsAt` é null/undefined, status = 'idle' e demais campos = 0.
 */
export function useCountdown(
  endsAt: string | Date | null | undefined,
  opts: Options = {},
): CountdownResult {
  const target = (() => {
    if (!endsAt) return null;
    const d = endsAt instanceof Date ? endsAt : new Date(endsAt);
    return Number.isFinite(d.getTime()) ? d : null;
  })();

  const startTarget = (() => {
    const s = opts.startsAt;
    if (!s) return null;
    const d = s instanceof Date ? s : new Date(s);
    return Number.isFinite(d.getTime()) ? d : null;
  })();

  const [, setTick] = useState(0);
  const timerRef = useRef<number | null>(null);

  // Re-agenda o próximo tick conforme o tempo restante.
  useEffect(() => {
    if (!target) return;

    const schedule = () => {
      const delta = target.getTime() - Date.now();
      const interval = nextInterval(delta);
      timerRef.current = window.setTimeout(() => {
        setTick((t) => (t + 1) % 1_000_000);
        // Continua agendando enquanto não terminou (e quando terminou,
        // emite um último tick e para — evita loop infinito).
        if (target.getTime() - Date.now() > -SEC) {
          schedule();
        }
      }, interval);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      } else {
        // Re-renderiza imediatamente ao voltar e re-agenda.
        setTick((t) => (t + 1) % 1_000_000);
        schedule();
      }
    };

    schedule();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [target?.getTime()]);

  if (!target) {
    return {
      status: 'idle',
      totalMs: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      target: null,
    };
  }

  const now = Date.now();
  const hasStartFuture = !!startTarget && startTarget.getTime() > now;
  // Quando ainda não começou, o "delta" relevante é até o start;
  // o consumidor pode optar por exibir "Inicia em ...".
  const deltaToEnd = target.getTime() - now;
  const deltaToShow = hasStartFuture
    ? startTarget!.getTime() - now
    : deltaToEnd;

  const status = computeStatus(deltaToEnd, hasStartFuture);
  const parts = decompose(deltaToShow);

  return {
    status,
    totalMs: Math.max(0, deltaToShow),
    ...parts,
    target,
  };
}

/**
 * Formata o resultado em string compacta com as 2 maiores unidades.
 * Ex.: "5d 12h", "12h 34m", "23m 12s", "0s".
 */
export function formatCountdownCompact(r: CountdownResult): string {
  if (r.status === 'idle') return '';
  const { days, hours, minutes, seconds } = r;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}
